import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, Navigation, X } from 'lucide-react';
import { Input } from './input';
import { Button } from './button';

const LocationPicker = ({ value, onChange, placeholder = "Search city...", className = "" }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update query when value prop changes
  useEffect(() => {
    if (value !== query) {
      setQuery(value || '');
    }
  }, [value]);

  // Fetch city suggestions using a free API
  const fetchCities = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Using GeoDB Cities API (free tier)
      const response = await fetch(
        `https://wft-geo-db.p.rapidapi.com/v1/geo/cities?namePrefix=${encodeURIComponent(searchQuery)}&limit=10&sort=-population`,
        {
          headers: {
            'X-RapidAPI-Key': 'demo', // This is a demo key - limited requests
            'X-RapidAPI-Host': 'wft-geo-db.p.rapidapi.com'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const cities = data.data?.map(city => ({
          id: city.id,
          city: city.city || city.name,
          country: city.country,
          countryCode: city.countryCode,
          region: city.region,
          display: `${city.city || city.name}, ${city.region ? city.region + ', ' : ''}${city.country}`
        })) || [];
        setSuggestions(cities);
      } else {
        // Fallback to local search if API fails
        fallbackSearch(searchQuery);
      }
    } catch (error) {
      console.error('City search error:', error);
      // Fallback to local search
      fallbackSearch(searchQuery);
    } finally {
      setLoading(false);
    }
  };

  // Fallback local city search with common world cities
  const fallbackSearch = (searchQuery) => {
    const worldCities = [
      // United States
      { city: 'New York', state: 'New York', country: 'United States', countryCode: 'US' },
      { city: 'Los Angeles', state: 'California', country: 'United States', countryCode: 'US' },
      { city: 'Chicago', state: 'Illinois', country: 'United States', countryCode: 'US' },
      { city: 'Houston', state: 'Texas', country: 'United States', countryCode: 'US' },
      { city: 'San Francisco', state: 'California', country: 'United States', countryCode: 'US' },
      { city: 'Miami', state: 'Florida', country: 'United States', countryCode: 'US' },
      { city: 'Seattle', state: 'Washington', country: 'United States', countryCode: 'US' },
      { city: 'Boston', state: 'Massachusetts', country: 'United States', countryCode: 'US' },
      // United Kingdom
      { city: 'London', country: 'United Kingdom', countryCode: 'UK' },
      { city: 'Manchester', country: 'United Kingdom', countryCode: 'UK' },
      { city: 'Birmingham', country: 'United Kingdom', countryCode: 'UK' },
      { city: 'Edinburgh', country: 'United Kingdom', countryCode: 'UK' },
      // France
      { city: 'Paris', country: 'France', countryCode: 'FR' },
      { city: 'Cannes', country: 'France', countryCode: 'FR' },
      { city: 'Nice', country: 'France', countryCode: 'FR' },
      { city: 'Lyon', country: 'France', countryCode: 'FR' },
      { city: 'Marseille', country: 'France', countryCode: 'FR' },
      // Germany
      { city: 'Berlin', country: 'Germany', countryCode: 'DE' },
      { city: 'Munich', country: 'Germany', countryCode: 'DE' },
      { city: 'Frankfurt', country: 'Germany', countryCode: 'DE' },
      { city: 'Hamburg', country: 'Germany', countryCode: 'DE' },
      // India - Major Cities
      { city: 'Mumbai', state: 'Maharashtra', country: 'India', countryCode: 'IN' },
      { city: 'Delhi', state: 'Delhi', country: 'India', countryCode: 'IN' },
      { city: 'New Delhi', state: 'Delhi', country: 'India', countryCode: 'IN' },
      { city: 'Bangalore', state: 'Karnataka', country: 'India', countryCode: 'IN' },
      { city: 'Bengaluru', state: 'Karnataka', country: 'India', countryCode: 'IN' },
      { city: 'Chennai', state: 'Tamil Nadu', country: 'India', countryCode: 'IN' },
      { city: 'Hyderabad', state: 'Telangana', country: 'India', countryCode: 'IN' },
      { city: 'Kolkata', state: 'West Bengal', country: 'India', countryCode: 'IN' },
      { city: 'Pune', state: 'Maharashtra', country: 'India', countryCode: 'IN' },
      { city: 'Ahmedabad', state: 'Gujarat', country: 'India', countryCode: 'IN' },
      { city: 'Jaipur', state: 'Rajasthan', country: 'India', countryCode: 'IN' },
      { city: 'Surat', state: 'Gujarat', country: 'India', countryCode: 'IN' },
      { city: 'Lucknow', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Kanpur', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Nagpur', state: 'Maharashtra', country: 'India', countryCode: 'IN' },
      { city: 'Indore', state: 'Madhya Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Thane', state: 'Maharashtra', country: 'India', countryCode: 'IN' },
      { city: 'Bhopal', state: 'Madhya Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Visakhapatnam', state: 'Andhra Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Patna', state: 'Bihar', country: 'India', countryCode: 'IN' },
      { city: 'Vadodara', state: 'Gujarat', country: 'India', countryCode: 'IN' },
      { city: 'Ghaziabad', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Ludhiana', state: 'Punjab', country: 'India', countryCode: 'IN' },
      { city: 'Agra', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Nashik', state: 'Maharashtra', country: 'India', countryCode: 'IN' },
      { city: 'Faridabad', state: 'Haryana', country: 'India', countryCode: 'IN' },
      { city: 'Meerut', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Rajkot', state: 'Gujarat', country: 'India', countryCode: 'IN' },
      { city: 'Varanasi', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Srinagar', state: 'Jammu & Kashmir', country: 'India', countryCode: 'IN' },
      { city: 'Amritsar', state: 'Punjab', country: 'India', countryCode: 'IN' },
      { city: 'Chandigarh', state: 'Chandigarh', country: 'India', countryCode: 'IN' },
      { city: 'Coimbatore', state: 'Tamil Nadu', country: 'India', countryCode: 'IN' },
      { city: 'Madurai', state: 'Tamil Nadu', country: 'India', countryCode: 'IN' },
      { city: 'Kochi', state: 'Kerala', country: 'India', countryCode: 'IN' },
      { city: 'Thiruvananthapuram', state: 'Kerala', country: 'India', countryCode: 'IN' },
      { city: 'Guwahati', state: 'Assam', country: 'India', countryCode: 'IN' },
      { city: 'Bhubaneswar', state: 'Odisha', country: 'India', countryCode: 'IN' },
      { city: 'Ranchi', state: 'Jharkhand', country: 'India', countryCode: 'IN' },
      { city: 'Gurgaon', state: 'Haryana', country: 'India', countryCode: 'IN' },
      { city: 'Noida', state: 'Uttar Pradesh', country: 'India', countryCode: 'IN' },
      { city: 'Mysore', state: 'Karnataka', country: 'India', countryCode: 'IN' },
      { city: 'Mangalore', state: 'Karnataka', country: 'India', countryCode: 'IN' },
      // Japan
      { city: 'Tokyo', country: 'Japan', countryCode: 'JP' },
      { city: 'Osaka', country: 'Japan', countryCode: 'JP' },
      // Australia
      { city: 'Sydney', state: 'New South Wales', country: 'Australia', countryCode: 'AU' },
      { city: 'Melbourne', state: 'Victoria', country: 'Australia', countryCode: 'AU' },
      { city: 'Brisbane', state: 'Queensland', country: 'Australia', countryCode: 'AU' },
      // Canada
      { city: 'Toronto', state: 'Ontario', country: 'Canada', countryCode: 'CA' },
      { city: 'Vancouver', state: 'British Columbia', country: 'Canada', countryCode: 'CA' },
      { city: 'Montreal', state: 'Quebec', country: 'Canada', countryCode: 'CA' },
      // Other Asian Countries
      { city: 'Singapore', country: 'Singapore', countryCode: 'SG' },
      { city: 'Kuala Lumpur', country: 'Malaysia', countryCode: 'MY' },
      { city: 'Penang', country: 'Malaysia', countryCode: 'MY' },
      { city: 'Beijing', country: 'China', countryCode: 'CN' },
      { city: 'Shanghai', country: 'China', countryCode: 'CN' },
      { city: 'Hong Kong', country: 'Hong Kong', countryCode: 'HK' },
      { city: 'Seoul', country: 'South Korea', countryCode: 'KR' },
      { city: 'Bangkok', country: 'Thailand', countryCode: 'TH' },
      { city: 'Jakarta', country: 'Indonesia', countryCode: 'ID' },
      { city: 'Manila', country: 'Philippines', countryCode: 'PH' },
      { city: 'Ho Chi Minh City', country: 'Vietnam', countryCode: 'VN' },
      // Middle East
      { city: 'Dubai', country: 'United Arab Emirates', countryCode: 'AE' },
      { city: 'Abu Dhabi', country: 'United Arab Emirates', countryCode: 'AE' },
      { city: 'Istanbul', country: 'Turkey', countryCode: 'TR' },
      { city: 'Ankara', country: 'Turkey', countryCode: 'TR' },
      { city: 'Hatay', country: 'Turkey', countryCode: 'TR' },
      { city: 'Izmir', country: 'Turkey', countryCode: 'TR' },
      { city: 'Tel Aviv', country: 'Israel', countryCode: 'IL' },
      { city: 'Jerusalem', country: 'Israel', countryCode: 'IL' },
      { city: 'Riyadh', country: 'Saudi Arabia', countryCode: 'SA' },
      { city: 'Doha', country: 'Qatar', countryCode: 'QA' },
      { city: 'Kuwait City', country: 'Kuwait', countryCode: 'KW' },
      // Europe
      { city: 'Rome', country: 'Italy', countryCode: 'IT' },
      { city: 'Milan', country: 'Italy', countryCode: 'IT' },
      { city: 'Madrid', country: 'Spain', countryCode: 'ES' },
      { city: 'Barcelona', country: 'Spain', countryCode: 'ES' },
      { city: 'Amsterdam', country: 'Netherlands', countryCode: 'NL' },
      { city: 'Brussels', country: 'Belgium', countryCode: 'BE' },
      { city: 'Zurich', country: 'Switzerland', countryCode: 'CH' },
      { city: 'Geneva', country: 'Switzerland', countryCode: 'CH' },
      { city: 'Vienna', country: 'Austria', countryCode: 'AT' },
      { city: 'Stockholm', country: 'Sweden', countryCode: 'SE' },
      { city: 'Oslo', country: 'Norway', countryCode: 'NO' },
      { city: 'Copenhagen', country: 'Denmark', countryCode: 'DK' },
      { city: 'Helsinki', country: 'Finland', countryCode: 'FI' },
      { city: 'Dublin', country: 'Ireland', countryCode: 'IE' },
      { city: 'Lisbon', country: 'Portugal', countryCode: 'PT' },
      { city: 'Warsaw', country: 'Poland', countryCode: 'PL' },
      { city: 'Prague', country: 'Czech Republic', countryCode: 'CZ' },
      { city: 'Budapest', country: 'Hungary', countryCode: 'HU' },
      { city: 'Athens', country: 'Greece', countryCode: 'GR' },
      { city: 'Moscow', country: 'Russia', countryCode: 'RU' },
      // Africa
      { city: 'Cairo', country: 'Egypt', countryCode: 'EG' },
      { city: 'Lagos', country: 'Nigeria', countryCode: 'NG' },
      { city: 'Johannesburg', country: 'South Africa', countryCode: 'ZA' },
      { city: 'Cape Town', country: 'South Africa', countryCode: 'ZA' },
      { city: 'Nairobi', country: 'Kenya', countryCode: 'KE' },
      // South America
      { city: 'São Paulo', state: 'São Paulo', country: 'Brazil', countryCode: 'BR' },
      { city: 'Rio de Janeiro', state: 'Rio de Janeiro', country: 'Brazil', countryCode: 'BR' },
      { city: 'Mexico City', country: 'Mexico', countryCode: 'MX' },
      { city: 'Buenos Aires', country: 'Argentina', countryCode: 'AR' },
      { city: 'Lima', country: 'Peru', countryCode: 'PE' },
      { city: 'Bogota', country: 'Colombia', countryCode: 'CO' },
      { city: 'Santiago', country: 'Chile', countryCode: 'CL' },
    ];

    const searchLower = searchQuery.toLowerCase();
    const filtered = worldCities
      .filter(c => 
        c.city.toLowerCase().includes(searchLower) ||
        c.country.toLowerCase().includes(searchLower) ||
        (c.state && c.state.toLowerCase().includes(searchLower))
      )
      .slice(0, 10)
      .map(c => ({
        ...c,
        display: c.state ? `${c.city}, ${c.state}, ${c.country}` : `${c.city}, ${c.country}`
      }));
    
    setSuggestions(filtered);
  };

  // Debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);

    // Also update parent with raw typed value (for validation)
    if (onChange) {
      onChange(value ? { display: value, city: value, country: '', countryCode: '' } : null);
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce API call
    debounceRef.current = setTimeout(() => {
      fetchCities(value);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (suggestion) => {
    const locationString = suggestion.display;
    setQuery(locationString);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call onChange with both display string and structured data
    if (onChange) {
      onChange({
        display: locationString,
        city: suggestion.city,
        country: suggestion.country,
        countryCode: suggestion.countryCode
      });
    }
  };

  // Get current location
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Reverse geocode using free API with English language preference
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json&accept-language=en`,
            {
              headers: {
                'Accept-Language': 'en-US,en;q=0.9'
              }
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            const city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || data.address?.state_district || '';
            const state = data.address?.state || '';
            const country = data.address?.country || '';
            
            // Build location string: City, State, Country
            let locationParts = [];
            if (city) locationParts.push(city);
            if (state && state !== city) locationParts.push(state);
            if (country) locationParts.push(country);
            
            const locationString = locationParts.join(', ') || data.display_name?.split(',').slice(0, 3).join(',').trim();
            
            setQuery(locationString);
            if (onChange) {
              onChange({
                display: locationString,
                city: city,
                state: state,
                country: country,
                countryCode: data.address?.country_code?.toUpperCase() || ''
              });
            }
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          alert('Could not determine your location. Please type your city.');
        } finally {
          setGeoLoading(false);
        }
      },
      (error) => {
        setGeoLoading(false);
        console.error('Geolocation error:', error);
        alert('Could not get your location. Please type your city.');
      },
      { timeout: 10000 }
    );
  };

  // Clear input
  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    if (onChange) {
      onChange(null);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
          <Input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.length >= 2 && setShowSuggestions(true)}
            placeholder={placeholder}
            className="pl-10 pr-8 bg-secondary"
          />
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {loading && (
            <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-emerald-400" />
          )}
        </div>
        
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleGetLocation}
          disabled={geoLoading}
          className="border-emerald-500/50 hover:bg-emerald-500/10 flex-shrink-0"
          title="Use my current location"
        >
          {geoLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4 text-emerald-400" />
          )}
        </Button>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id || index}
              type="button"
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-secondary flex items-center gap-3 border-b border-border last:border-0"
            >
              <MapPin className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <div>
                <span className="text-foreground font-medium">{suggestion.city}</span>
                {suggestion.state && <span className="text-muted-foreground">, {suggestion.state}</span>}
                <span className="text-muted-foreground">, {suggestion.country}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && query.length >= 2 && suggestions.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg p-4 text-center text-muted-foreground">
          No cities found. Try a different search term.
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
